import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import {
  CloudDone,
  CloudOff,
  Sync,
  Warning,
  Refresh,
} from '@mui/icons-material'
import { useList, useCustomMutation } from '@refinedev/core'

import {
  XeroConnectionStatus as ConnectionStatusType,
  XeroSyncResponse,
} from '../interfaces/accountsInterfaces'

const XeroConnectionStatus: React.FC = () => {
  const {
    data: statusResponse,
    isLoading: statusLoading,
    isError: statusError,
    refetch: refetchStatus,
  } = useList<ConnectionStatusType>({
    resource: 'xero/status',
  })

  const { mutate: syncContacts, isLoading: syncingContacts } = useCustomMutation<XeroSyncResponse>()
  const { mutate: syncInvoices, isLoading: syncingInvoices } = useCustomMutation<XeroSyncResponse>()
  const { mutate: syncPayments, isLoading: syncingPayments } = useCustomMutation<XeroSyncResponse>()
  const { mutate: syncAll, isLoading: syncingAll } = useCustomMutation<XeroSyncResponse>()

  const status = statusResponse?.data?.[0]
  const isAnySyncRunning = syncingContacts || syncingInvoices || syncingPayments || syncingAll

  const handleSyncContacts = () => {
    syncContacts(
      {
        url: '/xero/sync/contacts',
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          refetchStatus()
        },
      }
    )
  }

  const handleSyncInvoices = () => {
    syncInvoices(
      {
        url: '/xero/sync/invoices',
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          refetchStatus()
        },
      }
    )
  }

  const handleSyncPayments = () => {
    syncPayments(
      {
        url: '/xero/sync/payments',
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          refetchStatus()
        },
      }
    )
  }

  const handleSyncAll = () => {
    syncAll(
      {
        url: '/xero/sync/all',
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          refetchStatus()
        },
      }
    )
  }

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleString()
  }

  const formatCurrency = (value: number | null): string => {
    if (value === null || value === undefined) return '$0.00'
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const getConnectionIcon = () => {
    if (statusLoading) return <CircularProgress size={24} />
    if (statusError || !status?.is_connected) return <CloudOff color="error" />
    return <CloudDone color="success" />
  }

  const getConnectionColor = (): 'success' | 'error' | 'warning' | 'default' => {
    if (statusError || !status?.is_connected) return 'error'
    if (status?.sync_status?.includes('error')) return 'warning'
    return 'success'
  }

  if (statusLoading) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={2}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Checking Xero connection...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (statusError) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Alert severity="error">
            Failed to check Xero connection status. Please try again.
          </Alert>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetchStatus()}
            >
              Retry
            </Button>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" component="div">
            Xero Integration Status
          </Typography>
          <Box display="flex" alignItems="center">
            {getConnectionIcon()}
            <Chip
              label={status?.is_connected ? 'Connected' : 'Disconnected'}
              color={getConnectionColor()}
              size="small"
              sx={{ ml: 1 }}
            />
          </Box>
        </Box>

        {status?.is_connected ? (
          <Box>
            {/* Connection Details */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Connection Details
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>Tenant ID:</strong> {status.tenant_id || 'Unknown'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Token Expires:</strong> {formatDate(status.expires_at)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Connected Since:</strong> {formatDate(status.created_at)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Last Sync:</strong> {formatDate(status.last_sync)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Status:</strong> {status.sync_status || 'Unknown'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Data Summary */}
              {status.data_summary && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Data Summary
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          <strong>Contacts:</strong> {status.data_summary.contacts.toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Repeating Invoices:</strong> {status.data_summary.repeating_invoices.toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Invoices:</strong> {status.data_summary.invoices.toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Payments:</strong> {status.data_summary.payments.toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Linked Orgs:</strong> {status.data_summary.linked_orgs.toLocaleString()}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Recent Sync Activity */}
            {status.recent_sync_activity && status.recent_sync_activity.length > 0 && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Sync Activity
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Records</TableCell>
                          <TableCell>Started</TableCell>
                          <TableCell>Errors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {status.recent_sync_activity.map((activity, index) => (
                          <TableRow key={index}>
                            <TableCell>{activity.sync_type}</TableCell>
                            <TableCell>
                              <Chip
                                label={activity.status}
                                size="small"
                                color={activity.status === 'success' ? 'success' : activity.status === 'error' ? 'error' : 'warning'}
                              />
                            </TableCell>
                            <TableCell align="right">{activity.records_processed || 0}</TableCell>
                            <TableCell>
                              {activity.started_at ? new Date(activity.started_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {activity.errors && activity.errors !== 'No errors' ? (
                                <Typography variant="caption" color="error">
                                  {activity.errors.length > 30 ? `${activity.errors.substring(0, 30)}...` : activity.errors}
                                </Typography>
                              ) : (
                                'No errors'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Invoice Summary */}
            {status.invoice_summary && status.invoice_summary.length > 0 && (
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Invoice Summary
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Count</TableCell>
                              <TableCell align="right">Total Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {status.invoice_summary.map((invoice, index) => (
                              <TableRow key={index}>
                                <TableCell>{invoice.status}</TableCell>
                                <TableCell align="right">{invoice.count}</TableCell>
                                <TableCell align="right">{formatCurrency(invoice.total_amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Recurring Invoice Summary */}
                {status.recurring_invoice_summary && status.recurring_invoice_summary.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Recurring Invoice Summary
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Count</TableCell>
                                <TableCell align="right">Total Value</TableCell>
                                <TableCell>Next Invoice</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {status.recurring_invoice_summary.map((recurring, index) => (
                                <TableRow key={index}>
                                  <TableCell>{recurring.status}</TableCell>
                                  <TableCell align="right">{recurring.count}</TableCell>
                                  <TableCell align="right">{formatCurrency(recurring.total_value)}</TableCell>
                                  <TableCell>
                                    {recurring.next_invoice_date ?
                                      new Date(recurring.next_invoice_date).toLocaleDateString() : 'N/A'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Active Recurring Invoices */}
            {status.active_recurring_invoices && status.active_recurring_invoices.length > 0 && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Active Recurring Invoices
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Template</TableCell>
                          <TableCell>Contact</TableCell>
                          <TableCell>Schedule</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Next Date</TableCell>
                          <TableCell align="right">Generated</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {status.active_recurring_invoices.slice(0, 10).map((invoice, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {invoice.template_name && invoice.template_name.length > 25
                                ? `${invoice.template_name.substring(0, 22)}...`
                                : invoice.template_name || 'N/A'}
                            </TableCell>
                            <TableCell>
                              {invoice.contact_name && invoice.contact_name.length > 25
                                ? `${invoice.contact_name.substring(0, 22)}...`
                                : invoice.contact_name || 'N/A'}
                            </TableCell>
                            <TableCell>{invoice.schedule}</TableCell>
                            <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                            <TableCell>
                              {invoice.next_date ? new Date(invoice.next_date).toLocaleDateString() : 'N/A'}
                            </TableCell>
                            <TableCell align="right">{invoice.generated || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Billing Sync Queue */}
            {status.billing_sync_queue && status.billing_sync_queue.length > 0 && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Billing Sync Queue Status
                  </Typography>
                  <Stack spacing={1}>
                    {status.billing_sync_queue.map((queue, index) => (
                      <Typography key={index} variant="body2">
                        <strong>{queue.status}:</strong> {queue.count}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Manual Sync Operations */}
            <Typography variant="h6" gutterBottom>
              Manual Sync Operations
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={syncingContacts ? <CircularProgress size={16} /> : <Sync />}
                onClick={handleSyncContacts}
                disabled={isAnySyncRunning}
              >
                Sync Contacts
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={syncingInvoices ? <CircularProgress size={16} /> : <Sync />}
                onClick={handleSyncInvoices}
                disabled={isAnySyncRunning}
              >
                Sync Invoices
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={syncingPayments ? <CircularProgress size={16} /> : <Sync />}
                onClick={handleSyncPayments}
                disabled={isAnySyncRunning}
              >
                Sync Payments
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={syncingAll ? <CircularProgress size={16} /> : <Sync />}
                onClick={handleSyncAll}
                disabled={isAnySyncRunning}
                sx={{ ml: 'auto' }}
              >
                Sync All
              </Button>
            </Stack>

            {isAnySyncRunning && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Sync operation in progress. This may take a few moments...
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Xero is not connected. Set up the OAuth connection to enable billing sync.
            </Alert>
            <Typography variant="body2" color="textSecondary">
              Contact your system administrator to configure the Xero integration.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default XeroConnectionStatus
